import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import VouchCard from "../components/VouchCard";
import { db } from "../firebase";
import { ref, push, onValue } from "firebase/database";

const categories = [
  "Allgemein",
  "Service",
  "Support",
  "Zahlung",
  "Lieferung",
  "Sonstiges"
];

const steps = [
  { icon: "📝", label: "Formular ausfüllen" },
  { icon: "👁️", label: "Vorschau prüfen" },
  { icon: "🚀", label: "Absenden" }
];

export default function NewVouch() {
  const [form, setForm] = useState({
    name: "",
    userid: "",
    discord: "",
    message: "",
    rating: 5,
    category: categories[0],
    proof: "",
    email: "",
    consent: false,
    newsletter: false
  });
  const [error, setError] = useState("");
  const [recentVouches, setRecentVouches] = useState([]);
  const [topVouches, setTopVouches] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const vouchRef = ref(db, "vouches");
    const unsubscribe = onValue(vouchRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const all = Object.values(data);
        setRecentVouches(all.slice(-3).reverse());
        setTopVouches(all.filter(v => Number(v.rating) === 5).slice(-3).reverse());
      } else {
        setRecentVouches([]);
        setTopVouches([]);
      }
    });
    return () => unsubscribe();
  }, []);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  }

  function handleStarClick(r) {
    setForm({ ...form, rating: r });
  }

  function handleCategoryClick(cat) {
    setForm({ ...form, category: cat });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.message || !form.rating || !form.category || !form.consent) {
      setError("Bitte alle Pflichtfelder ausfüllen und Zustimmung geben.");
      return;
    }
    push(ref(db, "vouches"), {
      ...form,
      date: new Date().toLocaleDateString()
    })
      .then(() => {
        navigate("/");
      })
      .catch(() => setError("Fehler beim Absenden."));
  }

  // Fortschritt berechnen
  const requiredFields = ["name", "message", "rating", "category", "consent"];
  const filled = requiredFields.filter(f => form[f] && (f !== "consent" ? form[f].toString().trim() !== "" : form[f])).length;
  const progress = Math.round((filled / requiredFields.length) * 100);

  // Avatar Initialen
  function getInitials(name) {
    if (!name) return "👤";
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(ellipse at 80% 70%, #0a0a0c 0%, #151517 100%)",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      padding: "2.5rem 0"
    }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(340px, 520px) 40px minmax(340px, 520px)",
        gap: "2.5rem",
        width: "100%",
        maxWidth: "1200px",
        alignItems: "flex-start"
      }}>
        {/* Formular-Box */}
        <div style={{
          background: "#151517",
          borderRadius: "0.18rem",
          border: "1.5px solid #23232a",
          boxShadow: "0 0 32px #ff003c22",
          padding: "2.2rem 2rem 2rem 2rem",
          width: "100%"
        }}>
          {/* Header */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "1.2rem",
            marginBottom: "1.2rem",
            justifyContent: "center"
          }}>
            <span style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "2.5rem",
              height: "2.5rem",
              borderRadius: "50%",
              background: "#0a0a0c",
              border: "2px solid #ff003c",
              boxShadow: "0 0 12px #ff003c55"
            }}>
              <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <path d="M16 3L21.5 19.5L16 16L10.5 19.5L16 3Z" fill="#ff003c" />
                <path d="M16 16L21.5 19.5L16 29L10.5 19.5L16 16Z" fill="#fff" fillOpacity="0.12"/>
              </svg>
            </span>
            <span style={{
              color: "#ff003c",
              fontWeight: "bold",
              fontSize: "1.3rem",
              letterSpacing: "1px"
            }}>
              Bewertung abgeben
            </span>
          </div>
          {/* Schritte */}
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: "1.5rem",
            marginBottom: "1.2rem"
          }}>
            {steps.map((s, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "1.5rem",
                  color: progress >= ((i + 1) / steps.length) * 100 ? "#ff003c" : "#bdbdbd"
                }}>{s.icon}</div>
                <div style={{
                  color: progress >= ((i + 1) / steps.length) * 100 ? "#ff003c" : "#bdbdbd",
                  fontSize: "0.97rem"
                }}>{s.label}</div>
              </div>
            ))}
          </div>
          {/* Fortschrittsbalken */}
          <div style={{
            width: "100%",
            background: "#23232a",
            borderRadius: "0.18rem",
            height: "12px",
            marginBottom: "1.2rem",
            overflow: "hidden"
          }}>
            <div style={{
              width: `${progress}%`,
              background: "#ff003c",
              height: "100%",
              transition: "width 0.3s"
            }} />
          </div>
          {/* Vorteile */}
          <div style={{
            display: "flex",
            gap: "1.2rem",
            marginBottom: "1.2rem",
            justifyContent: "center"
          }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.7rem", color: "#ff003c" }}>⭐</div>
              <div style={{ color: "#fff", fontSize: "0.98rem" }}>Community-Feedback</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.7rem", color: "#ff003c" }}>🔒</div>
              <div style={{ color: "#fff", fontSize: "0.98rem" }}>Anonym & Sicher</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.7rem", color: "#ff003c" }}>🚀</div>
              <div style={{ color: "#fff", fontSize: "0.98rem" }}>Schnell & Einfach</div>
            </div>
          </div>
          {/* Formular */}
          <form className="vouch-form" onSubmit={handleSubmit} style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1.1rem 2rem"
          }}>
            {/* Avatar Vorschau */}
            <div style={{ gridColumn: "1 / span 2", display: "flex", alignItems: "center", gap: "1.2rem", marginBottom: "0.5rem" }}>
              <div style={{
                width: "2.7rem",
                height: "2.7rem",
                borderRadius: "0.25rem",
                background: "#19191b",
                color: "#ff003c",
                fontWeight: "bold",
                fontSize: "1.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1.5px solid #23232a"
              }}>
                {getInitials(form.name)}
              </div>
              <span style={{ color: "#bdbdbd", fontSize: "1.05rem" }}>
                Dein Avatar (Initialen)
              </span>
            </div>
            <label>
              Name <span style={{ color: "#ff003c" }}>*</span>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                maxLength={32}
                placeholder="Dein Name"
              />
              <div style={{ textAlign: "right", color: "#bdbdbd", fontSize: "0.95rem" }}>
                {form.name.length} / 32
              </div>
            </label>
            <label>
              Discord-Tag
              <input
                type="text"
                name="discord"
                value={form.discord}
                onChange={handleChange}
                maxLength={32}
                placeholder="z.B. Elyon#1234"
              />
              <div style={{ textAlign: "right", color: "#bdbdbd", fontSize: "0.95rem" }}>
                {form.discord.length} / 32
              </div>
            </label>
            <label>
              User-ID
              <input
                type="text"
                name="userid"
                value={form.userid}
                onChange={handleChange}
                maxLength={32}
                placeholder="z.B. 1234567890"
              />
              <div style={{ textAlign: "right", color: "#bdbdbd", fontSize: "0.95rem" }}>
                {form.userid.length} / 32
              </div>
            </label>
            {/* Kategorie als Buttons */}
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {categories.map(cat => (
                <button
                  type="button"
                  key={cat}
                  onClick={() => handleCategoryClick(cat)}
                  style={{
                    background: form.category === cat ? "#ff003c" : "#23232a",
                    color: form.category === cat ? "#fff" : "#bdbdbd",
                    border: "none",
                    borderRadius: "0.18rem",
                    padding: "0.5rem 1.1rem",
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: "1rem"
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
            {/* Sternebewertung */}
            <div style={{ gridColumn: "1 / span 2", margin: "0.5rem 0" }}>
              <span style={{ color: "#ff003c", fontWeight: "bold", marginRight: "1rem" }}>Bewertung *</span>
              {[1, 2, 3, 4, 5].map(r => (
                <span
                  key={r}
                  onClick={() => handleStarClick(r)}
                  style={{
                    fontSize: "1.7rem",
                    color: r <= form.rating ? "#ff003c" : "#23232a",
                    cursor: "pointer",
                    marginRight: "0.2rem",
                    userSelect: "none"
                  }}
                  title={`${r} Sterne`}
                >★</span>
              ))}
            </div>
            <label style={{ gridColumn: "1 / span 2" }}>
              Nachricht <span style={{ color: "#ff003c" }}>*</span>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                maxLength={300}
                placeholder="Deine Bewertung..."
                style={{ minHeight: "90px" }}
              />
              <div style={{
                textAlign: "right",
                color: "#bdbdbd",
                fontSize: "0.95rem"
              }}>
                {form.message.length} / 300 Zeichen
              </div>
            </label>
            <label>
              Proof-Link (optional)
              <input
                type="url"
                name="proof"
                value={form.proof}
                onChange={handleChange}
                maxLength={128}
                placeholder="z.B. https://imgur.com/..."
              />
              <div style={{ textAlign: "right", color: "#bdbdbd", fontSize: "0.95rem" }}>
                {form.proof.length} / 128
              </div>
            </label>
            <label>
              E-Mail (optional)
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                maxLength={64}
                placeholder="(Wird nicht veröffentlicht)"
              />
              <div style={{ textAlign: "right", color: "#bdbdbd", fontSize: "0.95rem" }}>
                {form.email.length} / 64
              </div>
            </label>
            {/* Newsletter */}
            <div style={{ gridColumn: "1 / span 2", display: "flex", alignItems: "center", gap: "0.7rem" }}>
              <input
                type="checkbox"
                name="newsletter"
                checked={form.newsletter}
                onChange={handleChange}
              />
              <span style={{ color: "#bdbdbd", fontSize: "0.98rem" }}>
                Updates & Aktionen per Mail erhalten (optional)
              </span>
            </div>
            {/* Consent */}
            <div style={{ gridColumn: "1 / span 2", display: "flex", alignItems: "center", gap: "0.7rem", marginTop: "0.5rem" }}>
              <input
                type="checkbox"
                name="consent"
                checked={form.consent}
                onChange={handleChange}
                required
              />
              <span style={{ color: "#bdbdbd", fontSize: "0.98rem" }}>
                Ich stimme zu, dass meine Bewertung veröffentlicht wird.
              </span>
            </div>
            {error && <div className="form-error" style={{ gridColumn: "1 / span 2" }}>{error}</div>}
            <button type="submit" className="form-submit" style={{ gridColumn: "1 / span 2" }}>
              Bewertung absenden
            </button>
          </form>
        </div>
        {/* Spacer */}
        <div />
        {/* Rechte Spalte: Vorschau, Tipps, FAQ, Inspiration */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          minWidth: "340px",
          maxWidth: "520px"
        }}>
          {/* Vorschau */}
          <div>
            <div className="form-preview-title" style={{
              marginBottom: "1.2rem",
              textAlign: "center",
              color: "#eaeaea",
              opacity: 0.85,
              fontSize: "1.15rem",
              fontWeight: "bold"
            }}>
              Vorschau deiner Bewertung:
            </div>
            <VouchCard vouch={{
              name: form.name || "Dein Name",
              userid: form.userid,
              discord: form.discord,
              message: form.message || "Deine Bewertung...",
              rating: form.rating,
              date: new Date().toLocaleDateString(),
              category: form.category,
              proof: form.proof
            }} idx={1} />
            <div style={{
              marginTop: "1.2rem",
              color: "#bdbdbd",
              fontSize: "1.08rem",
              textAlign: "center"
            }}>
              So wird deine Bewertung nach dem Absenden angezeigt.
            </div>
          </div>
          {/* Tipps & FAQ nebeneinander */}
          <div style={{
            display: "flex",
            gap: "1.2rem",
            flexWrap: "wrap"
          }}>
            <div style={{
              flex: 1,
              background: "#23232a",
              borderRadius: "0.18rem",
              padding: "1.2rem",
              color: "#fff",
              minWidth: "180px"
            }}>
              <div style={{ color: "#ff003c", fontWeight: "bold", fontSize: "1.08rem", marginBottom: "0.7rem", textAlign: "center" }}>
                Tipps für eine hilfreiche Bewertung
              </div>
              <ul style={{ margin: 0, paddingLeft: "1.2rem", fontSize: "0.98rem" }}>
                <li>Beschreibe deine Erfahrung möglichst konkret</li>
                <li>Bleibe sachlich und freundlich</li>
                <li>Gib an, was dir besonders gefallen oder gefehlt hat</li>
              </ul>
            </div>
            <div style={{
              flex: 1,
              background: "#23232a",
              borderRadius: "0.18rem",
              padding: "1.2rem",
              color: "#fff",
              minWidth: "180px"
            }}>
              <div style={{ color: "#ff003c", fontWeight: "bold", fontSize: "1.08rem", marginBottom: "0.7rem", textAlign: "center" }}>
                FAQ
              </div>
              <div style={{ fontSize: "0.98rem", marginBottom: "0.5rem" }}>
                <b>Ist meine Bewertung anonym?</b><br />
                Ja, du kannst ohne Discord-Tag oder E-Mail bewerten.
              </div>
              <div style={{ fontSize: "0.98rem", marginBottom: "0.5rem" }}>
                <b>Wie schnell erscheint meine Bewertung?</b><br />
                Sofort nach dem Absenden auf der Startseite.
              </div>
              <div style={{ fontSize: "0.98rem" }}>
                <b>Kann ich meine Bewertung löschen?</b><br />
                Schreibe uns eine E-Mail, falls du deine Bewertung entfernen möchtest.
              </div>
            </div>
          </div>
          {/* Feedback & Support */}
          <div style={{
            background: "#23232a",
            borderRadius: "0.18rem",
            padding: "1.2rem",
            color: "#fff",
            textAlign: "center"
          }}>
            <div style={{ color: "#ff003c", fontWeight: "bold", fontSize: "1.08rem", marginBottom: "0.7rem" }}>
              Feedback & Support
            </div>
            <div>
              Du hast Fragen oder Probleme? Schreibe uns an <a href="mailto:raspyy.dev@gmail.com" style={{ color: "#ff003c" }}>raspyy.dev@gmail.com</a>
            </div>
          </div>
        </div>
      </div>
      {/* Responsive: Staple bei kleinen Bildschirmen */}
      <style>{`
        @media (max-width: 1200px) {
          div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
            gap: 2.5rem !important;
          }
          div[style*="minWidth: 340px"][style*="maxWidth: 520px"] {
            margin-left: 0 !important;
            max-width: 100vw !important;
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
}
